import { useEffect, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { Camera, X, Send, ChevronDown, ChevronUp, Building2, FileText } from "lucide-react";
import { SiteForms } from "../components/agent/SiteForms";
import { AgentLogbook } from "../components/agent/AgentLogbook";
import { AgentKeys } from "../components/agent/AgentKeys";
import { AgentVisitors } from "../components/agent/AgentVisitors";
import { SearchSelect } from "../components/SearchSelect";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const REPORT_TYPES = [
  { code: "main_courante", label: "Main courante", cls: "bg-sky-100 text-sky-800 border-sky-300" },
  { code: "incident", label: "Incident", cls: "bg-rose-100 text-rose-800 border-rose-300" },
  { code: "ronde", label: "Ronde", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { code: "autre", label: "Autre", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  { code: "form", label: "Formulaire", cls: "bg-amber-100 text-amber-900 border-amber-300" },
];
export const reportType = (c) => REPORT_TYPES.find((t) => t.code === c) || REPORT_TYPES[3];

export default function AgentReports({ off = [] }) {
  const [reports, setReports] = useState([]);
  const [sites, setSites] = useState([]);
  const load = async () => {
    const [r, s] = await Promise.all([http.get("/agent-app/reports"), http.get("/agent-app/report-sites")]);
    setReports(r.data); setSites(s.data);
  };
  const [view, setView] = useState("reports");
  const views = [["reports", "Rapports"], ["logbook", "Main courante", "main_courante"], ["visitors", "Visiteurs", "consignes"], ["keys", "Clés", "consignes"]].filter(([, , m]) => !m || !off.includes(m));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-6">
      {views.length > 1 && <div className={`grid ${["", "", "grid-cols-2", "grid-cols-3", "grid-cols-4"][views.length]} gap-1 bg-slate-200/70 rounded-xl p-1`} data-testid="agent-reports-switch">
        {views.map(([k, l]) => (
          <button key={k} data-testid={`agent-reports-view-${k}`} onClick={() => setView(k)} className={`rounded-lg py-2 text-xs font-bold leading-tight transition-colors ${view === k ? "bg-white text-[#0B2A7A] shadow" : "text-slate-600"}`}>{l}</button>
        ))}
      </div>}
      {view === "visitors" ? <AgentVisitors /> : view === "keys" ? <AgentKeys /> : view === "logbook" ? <AgentLogbook /> : <>
      {!off.includes("report_templates") && <SiteForms onSent={load} />}
      <ReportForm sites={sites} onSent={load} />
      <section>
        <h2 className="font-display text-lg font-extrabold mb-2">Mes rapports</h2>
        <div className="space-y-2" data-testid="agent-reports-history">
          {reports.length === 0 && <div className="bg-white border border-slate-200 rounded-xl p-5 text-center text-sm text-slate-500">Aucun rapport envoyé.</div>}
          {reports.map((r) => <ReportItem key={r.id} r={r} />)}
        </div>
      </section>
      </>}
    </div>
  );
}

function ReportForm({ sites, onSent }) {
  const empty = { type: "incident", site_id: "", title: "", body: "" };
  const [f, setF] = useState(empty);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const addPhotos = (files) => setPhotos((p) => [...p, ...Array.from(files)].slice(0, 3));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      photos.forEach((p) => fd.append("photos", p));
      await http.post("/agent-app/reports", fd);
      setF(empty); setPhotos([]);
      setMsg({ ok: true, text: "Rapport envoyé ✓" });
      onSent();
    } catch (e2) { setMsg({ ok: false, text: formatApiError(e2) }); }
    finally { setBusy(false); setTimeout(() => setMsg(null), 4000); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="agent-report-form">
      <h2 className="font-display text-lg font-extrabold">Nouveau rapport</h2>
      <div className="grid grid-cols-2 gap-2">
        {REPORT_TYPES.filter((t) => t.code !== "form" && t.code !== "main_courante").map((t) => (
          <button type="button" key={t.code} data-testid={`report-type-${t.code}`} onClick={() => setF({ ...f, type: t.code })}
            className={`rounded-lg border py-2 text-sm font-bold transition-colors ${f.type === t.code ? t.cls + " ring-2 ring-offset-1 ring-sky-400" : "bg-white border-slate-200 text-slate-600"}`}>{t.label}</button>
        ))}
      </div>
      <SearchSelect data-testid="report-site" value={f.site_id} onChange={(e) => setF({ ...f, site_id: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white">
        <option value="">— Site (facultatif) —</option>
        {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </SearchSelect>
      <input data-testid="report-title" required placeholder="Titre *" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" />
      <textarea data-testid="report-body" required rows={5} placeholder="Décrivez les faits (heure, lieu, personnes, actions menées)… *" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" />
      <div className="flex flex-wrap gap-2 items-center">
        {photos.map((p, i) => (
          <div key={i} className="relative h-16 w-16 rounded-lg overflow-hidden border border-slate-200">
            <img src={URL.createObjectURL(p)} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 bg-slate-900/70 rounded-full p-0.5 text-white"><X className="h-3 w-3" /></button>
          </div>
        ))}
        {photos.length < 3 && (
          <label className="h-16 w-16 rounded-lg border-2 border-dashed border-slate-300 grid place-items-center text-slate-500 cursor-pointer hover:border-sky-400" data-testid="report-add-photo">
            <Camera className="h-5 w-5" />
            <input data-testid="report-photo-input" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" multiple className="hidden" onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
          </label>
        )}
        <span className="text-[11px] text-slate-400">{photos.length}/3 photos</span>
      </div>
      {msg && <div data-testid="report-msg" className={`text-sm rounded-md px-3 py-2 ${msg.ok ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-700"}`}>{msg.text}</div>}
      <button data-testid="report-submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-500 text-white font-bold rounded-lg py-3 disabled:opacity-60 active:scale-[.98] transition-transform">
        <Send className="h-4 w-4" /> {busy ? "Envoi…" : "Envoyer le rapport"}
      </button>
    </form>
  );
}

function ReportItem({ r }) {
  const [open, setOpen] = useState(false);
  const t = reportType(r.type);
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`agent-report-${r.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left px-4 py-3 flex items-center gap-3">
        <FileText className="h-4 w-4 text-slate-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2"><span className={`border rounded text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 ${t.cls}`}>{t.label}</span><span className="font-semibold text-sm truncate">{r.title}</span></div>
          <div className="text-xs text-slate-500 mt-0.5">{new Date(r.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}{r.site_name && <> · <Building2 className="h-3 w-3 inline -mt-0.5" /> {r.site_name}</>}{r.read_at && " · Lu par l'administrateur"}</div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-slate-700 whitespace-pre-line border-t border-slate-100 pt-3">
          {r.body}
          {r.photo_file_ids?.length > 0 && (
            <div className="flex gap-2 mt-3">
              {r.photo_file_ids.map((fid) => <img key={fid} src={`${BACKEND}/api/agent-app/reports/${r.id}/photos/${fid}`} alt="" className="h-20 w-20 rounded-lg object-cover border border-slate-200" />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
